// src/WebSocketContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

type WebSocketContextType = {
  socket: WebSocket | null;
  isConnected: boolean;
  playerId: number;
  reconnect: () => void;
};

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);

  const [playerId, setPlayerId] = useState(-1);

  const socketRef = useRef<WebSocket | null>(null);

  const [forceReconnect, setForceReconnect] = useState(false);

  useEffect(() => {
    const explicitUrl = process.env.REACT_APP_WS_URL;
    const host = process.env.REACT_APP_WS_HOST || window.location.hostname;
    const port = process.env.REACT_APP_WS_PORT || "6970";
    const protocol = "wss";
    const socketUrl = explicitUrl || `${protocol}://${host}${port ? `:${port}` : ""}`;

    const socket = new WebSocket(socketUrl);
    socketRef.current = socket;

    socket.onmessage = (event) => {
      if (event) {
        const message = JSON.parse(event.data)

        if (message.kind === "Hello") {
          setPlayerId(message.id)
        }
      }
    };

    socket.onopen = () => {
      console.log("✅ WebSocket connected");
      setIsConnected(true);
    };

    socket.onclose = () => {
      console.log("❌ WebSocket disconnected");
      setIsConnected(false);
    };

    socket.onerror = (err) => {
      console.error("⚠️ WebSocket error:", err);
    };

    return () => {
      socket.close();
      setPlayerId(-1);
    };
  }, [forceReconnect]);

  function reconnect() {
    setForceReconnect(!forceReconnect)
  }

  return (
    <WebSocketContext.Provider value={{
        socket: socketRef.current,
        isConnected: isConnected,
        playerId: playerId,
        reconnect: reconnect,
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) throw new Error("useWebSocket must be used inside WebSocketProvider");
  return context;
};
